import { useEffect, useState } from 'react';
import { formatMm, mm, type Um } from '@scs/domain-engine';
import { evalMm, isExpression } from './mmexpr';

export function Logo({ size = 22 }: { size?: number }) {
  return <img src="/favicon.svg" width={size} height={size} alt="" />;
}

/**
 * Numeric millimetre input. Accepts "," or "." as decimal separator and simple arithmetic
 * ("800+18", "543-18", "(600-36)/2"); commits on Enter/blur; Escape reverts; ArrowUp/Down step
 * (Shift ×10). Invalid input is shown and never committed.
 */
export function MmInput({
  value,
  onCommit,
  min = 0,
  max = 10_000,
  step = 1,
  disabled,
  label,
}: {
  value: Um;
  onCommit(v: Um): void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label: string;
}) {
  const [text, setText] = useState(formatMm(value));
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setText(formatMm(value));
    setInvalid(false);
  }, [value]);

  const preview = isExpression(text) ? (evalMm(text) ?? '?') : null;
  const commit = (raw: string) => {
    const n = evalMm(raw) ?? NaN;
    if (!Number.isFinite(n) || n < min || n > max) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const um = mm(n);
    if (um !== value) onCommit(um);
    else setText(formatMm(value));
  };

  return (
    <div className={`num${invalid ? ' invalid' : ''}`} title={invalid ? `Dozvoljeno ${min}–${max} mm` : 'Može i račun, npr. 800+18'}>
      <input
        aria-label={label}
        inputMode="text"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
          else if (e.key === 'Escape') {
            setText(formatMm(value));
            setInvalid(false);
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const cur = evalMm(text) ?? 0;
            const d = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            const next = Math.min(max, Math.max(min, Math.round((cur + d) * 10) / 10));
            setText(String(next));
            commit(String(next));
          }
          e.stopPropagation();
        }}
      />
      {preview !== null && <span className="num-preview mono">= {preview}</span>}
      <span className="unit">mm</span>
    </div>
  );
}

export function IntInput({ value, onCommit, min, max, label, disabled }: { value: number; onCommit(v: number): void; min: number; max: number; label: string; disabled?: boolean }) {
  return (
    <div className="num">
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const n = Math.round(Number(e.target.value));
          if (Number.isFinite(n) && n >= min && n <= max) onCommit(n);
        }}
      />
      <span className="unit">kom</span>
    </div>
  );
}
